import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CtaBanner } from "@/components/site/CtaBanner";
import { Reveal } from "@/components/site/Reveal";
import {
  ChevronRight,
  Phone,
  ArrowRight,
  CheckCircle2,
  MapPin,
  Clock,
  Award,
  Shield,
} from "lucide-react";
import { findLocation, locationPages, type LocationPage } from "@/lib/location-data";

export const Route = createFileRoute("/epoxy-flooring/$city")({
  loader: ({ params }) => {
    const loc = findLocation(params.city);
    if (!loc) throw notFound();
    return { loc };
  },
  head: ({ loaderData, params }) => {
    const loc = loaderData?.loc;
    if (!loc) return { meta: [{ title: "Epoxy Flooring NJ" }] };
    const url = `https://blancopros.com/epoxy-flooring/${params.city}`;
    return {
      meta: [
        { title: loc.metaTitle },
        { name: "description", content: loc.metaDescription },
        { property: "og:image", content: "https://blancopros.com/og-image.jpg" },
        { name: "twitter:image", content: "https://blancopros.com/og-image.jpg" },
        { property: "og:title", content: loc.metaTitle },
        { property: "og:description", content: loc.metaDescription },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: loc.metaTitle },
        { name: "twitter:description", content: loc.metaDescription },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": `${url}#business`,
            name: `Blanco Pro Services, Epoxy Flooring ${loc.city}`,
            url,
            telephone: "+1-908-895-6133",
            email: "info@blancopros.com",
            image: "https://blancopros.com/og-image.jpg",
            priceRange: "$$",
            address: {
              "@type": "PostalAddress",
              addressRegion: "NJ",
              addressCountry: "US",
            },
            areaServed: [
              { "@type": "City", name: loc.city, containedInPlace: { "@type": "AdministrativeArea", name: `${loc.county} County, NJ` } },
            ],
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "5.0",
              reviewCount: "27",
            },
            openingHours: "Mo-Sa 07:30-16:30",
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: loc.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://blancopros.com/" },
              { "@type": "ListItem", position: 2, name: "Service Area", item: "https://blancopros.com/service-area" },
              { "@type": "ListItem", position: 3, name: `Epoxy Flooring ${loc.city}, NJ`, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="pt-40 pb-20 text-center px-4">
      <h1 className="font-display text-4xl font-bold mb-4">Location not found</h1>
      <p className="text-muted-foreground mb-6">We still likely serve your area, give us a call.</p>
      <Link to="/service-area" className="text-foreground underline">See all service areas</Link>
    </div>
  ),
  component: LocationPageView,
});

function LocationPageView() {
  const { loc } = Route.useLoaderData() as { loc: LocationPage };
  const related = locationPages.filter((l) => l.slug !== loc.slug).slice(0, 4);

  return (
    <>
      {/* HERO */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-hero">
        <div className="max-w-6xl mx-auto">
          <nav aria-label="Breadcrumb" className="flex items-center flex-wrap gap-1 text-xs uppercase tracking-[0.25em] text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/service-area" className="hover:text-foreground transition-colors">Service Area</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-foreground">{loc.city}, NJ</span>
          </nav>

          <Reveal>
            <div className="text-xs uppercase tracking-[0.25em] text-gold mb-4 inline-flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" /> {loc.county} County · {loc.region}
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] mb-6">
              Epoxy Flooring in <span className="bg-gradient-gold bg-clip-text text-transparent">{loc.city}, NJ</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mb-8">
              {loc.hook}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="tel:9088956133"
                className="inline-flex items-center justify-center gap-2 bg-foreground text-background px-6 py-3 rounded-full text-sm font-medium hover:bg-gradient-gold hover:text-gold-foreground transition-all shadow-elegant"
              >
                <Phone className="w-4 h-4" /> Call 908-895-6133
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 border-2 border-foreground px-6 py-3 rounded-full text-sm font-medium hover:bg-foreground hover:text-background transition-all"
              >
                Free {loc.city} Estimate <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>

          {/* Trust strip */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Award, label: "5.0★ Rated", sub: "27+ reviews" },
              { icon: Shield, label: "2-Year Workmanship Warranty", sub: "Standard on every job" },
              { icon: Clock, label: "Fast response", sub: "Same-week consults" },
              { icon: MapPin, label: "Licensed & Insured", sub: "NJ HIC + $2M liability" },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl border border-border bg-card p-5">
                <s.icon className="w-5 h-5 text-gold mb-2" />
                <div className="font-semibold text-sm">{s.label}</div>
                <div className="text-xs text-muted-foreground">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Why {loc.city} chooses us</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-8">
              The best epoxy flooring contractor near you in {loc.city}
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {loc.whyChoose.map((reason, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="h-full rounded-2xl border border-border bg-card p-6 shadow-soft">
                  <div className="w-10 h-10 rounded-full bg-gradient-gold flex items-center justify-center mb-4 font-display font-bold text-gold-foreground">
                    {i + 1}
                  </div>
                  <p className="text-foreground/90 leading-relaxed">{reason}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-3">
              Epoxy floor services we install in {loc.city}
            </h2>
            <p className="text-muted-foreground mb-8 max-w-3xl">
              Every system below is installed by our in-house crew with premium materials and a 2-year workmanship warranty, no subcontracting.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-3">
            {loc.primaryServices.map((service, i) => (
              <Reveal key={service} delay={i * 40}>
                <div className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border">
                  <CheckCircle2 className="w-5 h-5 text-gold mt-0.5 shrink-0" />
                  <span className="text-foreground/90">{service}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MICRO CLIMATE / SUBSTRATE */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Built for {loc.city}'s conditions</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">
              Why surface prep matters more here
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed border-l-2 border-gold pl-6">
              {loc.microClimate}
            </p>
          </Reveal>
        </div>
      </section>

      {/* RECENT PROJECTS */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-8">
              Recent projects in {loc.county} County
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-3 gap-5">
            {loc.recentProjects.map((p, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="h-full rounded-2xl border border-border bg-card p-6 shadow-soft">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-gold mb-2">{p.sqft}</div>
                  <h3 className="font-display text-lg font-bold mb-2 leading-tight">{p.type}</h3>
                  <p className="text-sm text-muted-foreground">{p.neighborhood}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-8">
              {loc.city} epoxy flooring FAQs
            </h2>
          </Reveal>
          <div className="space-y-4">
            {loc.faqs.map((f) => (
              <Reveal key={f.q}>
                <details className="group rounded-2xl border border-border bg-card p-6 open:shadow-soft transition-all">
                  <summary className="cursor-pointer font-display text-lg font-semibold list-none flex items-center justify-between gap-4">
                    {f.q}
                    <ChevronRight className="w-4 h-4 shrink-0 transition-transform group-open:rotate-90" />
                  </summary>
                  <p className="mt-3 text-muted-foreground leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LEAD CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <div className="rounded-3xl border border-border bg-card p-8 md:p-12 text-center shadow-elegant">
              <div className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Free {loc.city} Estimate</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
                Get a quote for your {loc.city} project today
              </h2>
              <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                Licensed, insured, and serving {loc.city}. Same-week consultations available. ZIP codes served: {loc.zipCodes.join(", ")}.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href="tel:9088956133" className="inline-flex items-center justify-center gap-2 bg-foreground text-background px-6 py-3 rounded-full text-sm font-medium hover:bg-gradient-gold hover:text-gold-foreground transition-all">
                  <Phone className="w-4 h-4" /> 908-895-6133
                </a>
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 border-2 border-foreground px-6 py-3 rounded-full text-sm font-medium hover:bg-foreground hover:text-background transition-all">
                  Request a Free Quote <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* OTHER LOCATIONS */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-6">Other NJ areas we serve</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
            {related.map((r, i) => (
              <Reveal key={r.slug} delay={i * 60}>
                <Link
                  to="/epoxy-flooring/$city"
                  params={{ city: r.slug }}
                  className="block rounded-2xl border border-border bg-card p-5 hover:shadow-elegant hover:-translate-y-1 transition-all"
                >
                  <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-1">{r.county} County</div>
                  <div className="font-display text-lg font-bold mb-1">{r.city}, NJ</div>
                  <span className="inline-flex items-center gap-1 text-xs text-foreground/70">
                    View epoxy services <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/service-area" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:gap-3 transition-all">
              See all 565+ NJ towns we serve <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
